// /opt/isp_erp/frontend/src/pages/CallCenter.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  Alert,
  Snackbar,
  Autocomplete,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
  Rating
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  AssignmentInd as AssignmentIndIcon,
  ArrowUpward as ArrowUpwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/useAuth';
import {
  getCallCenterTickets,
  getCallCenterStats,
  getTechnicianStats,
  createCallCenterTicket,
  assignToN1,
  assignToN2,
  assignToSupervisor,
  escalateToN2,
  escalateToSupervisor,
  resolveCallCenterTicket,
  closeCallCenterTicket,
  addInteraction,
  searchCustomers,
  getCustomerTickets,
  updateCallCenterTicket,
  deleteCallCenterTicket
} from '../api/callcenter.api';
import FollowUpNotifications from '../components/tickets/FollowUpNotifications';
import { getUsuarios } from '../api/roles.api';

// =============== CONSTANTES ===============

const statusColors = {
  nuevo: 'default',
  en_espera: 'warning',
  asignado_n1: 'primary',
  asignado_n2: 'secondary',
  asignado_supervisor: 'error',
  en_progreso: 'info',
  escalado_n2: 'warning',
  escalado_supervisor: 'error',
  resuelto: 'success',
  cerrado: 'grey',
  cancelado: 'error',
  reabierto: 'warning'
};

const statusLabels = {
  nuevo: 'Nuevo',
  en_espera: 'En Espera',
  asignado_n1: 'Asignado N1',
  asignado_n2: 'Asignado N2',
  asignado_supervisor: 'Asignado Supervisor',
  en_progreso: 'En Progreso',
  escalado_n2: 'Escalado a N2',
  escalado_supervisor: 'Escalado a Supervisor',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
  cancelado: 'Cancelado',
  reabierto: 'Reabierto'
};

const canalLabels = {
  llamada: '📞 Llamada',
  whatsapp: '💬 WhatsApp',
  correo: '📧 Correo',
  chat: '💭 Chat',
  presencial: '🏢 Presencial',
  redes_sociales: '🌐 Redes Sociales'
};

const canalIcons = {
  llamada: <PhoneIcon />,
  whatsapp: <WhatsAppIcon />,
  correo: <EmailIcon />,
  chat: <ChatIcon />,
  presencial: <PersonIcon />,
  redes_sociales: <AssessmentIcon />
};

const problemTypeLabels = {
  conexion: '🔌 Problema de Conexión',
  velocidad: '⚡ Problema de Velocidad',
  equipo: '📡 Problema de Equipo',
  facturacion: '💰 Facturación',
  soporte_tecnico: '🛠️ Soporte Técnico',
  configuracion: '⚙️ Configuración',
  reclamo: '📢 Reclamo',
  consulta: '❓ Consulta',
  instalacion: '🔧 Instalación',
  otro: '📝 Otro'
};

const priorityColors = {
  baja: 'success',
  media: 'info',
  alta: 'warning',
  critica: 'error'
};

const satisfactionLabels = {
  1: '⭐ Muy Malo',
  2: '⭐⭐ Malo',
  3: '⭐⭐⭐ Regular',
  4: '⭐⭐⭐⭐ Bueno',
  5: '⭐⭐⭐⭐⭐ Excelente'
};

// =============== COMPONENTE PRINCIPAL ===============

export default function CallCenter() {
  const { user, isAdmin, isSupervisor, isSoporte, hasRole } = useAuth();

  // Estados para tickets
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCanal, setFilterCanal] = useState('');
  const [filterProblema, setFilterProblema] = useState('');
  const [filterPrioridad, setFilterPrioridad] = useState('');
  const [filterTechnician, setFilterTechnician] = useState('');
  const [techniciansList, setTechniciansList] = useState([]);

  // Estados para estadísticas
  const [stats, setStats] = useState(null);
  const [techStats, setTechStats] = useState([]);

  // Estado para diálogos
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [interactionDialogOpen, setInteractionDialogOpen] = useState(false);
  const [customerHistoryDialogOpen, setCustomerHistoryDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Estado para asignación
  const [assignType, setAssignType] = useState('n1');
  const [assignData, setAssignData] = useState({
    technician_id: '',
    notes: '',
    reason: ''
  });

  // Estado para edición
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    canal_contacto: '',
    tipo_problema: '',
    prioridad: '',
    status: ''
  });

  // Estado para resolución
  const [resolveData, setResolveData] = useState({
    resolution_notes: '',
    satisfaction_score: 5,
    quality_score: 5,
    resolution_type: 'estandar'
  });

  // Estado para cierre
  const [closeData, setCloseData] = useState({
    closure_notes: '',
    satisfaction_score: 5,
    follow_up_required: false,
    follow_up_date: null,
    follow_up_notes: ''
  });

  // Estado para interacción
  const [interactionData, setInteractionData] = useState({
    tipo: 'nota',
    description: '',
    duracion_segundos: '',
    resultado: ''
  });

  // Estado para creación de ticket
  const [newTicket, setNewTicket] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_phone_alt: '',
    title: '',
    description: '',
    canal_contacto: 'llamada',
    tipo_problema: 'conexion',
    prioridad: 'media',
    assign_to_n1_id: '',
    assign_to_n2_id: ''
  });

  // Estado para clientes
  const [customers, setCustomers] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerHistory, setCustomerHistory] = useState([]);

  // Estado para técnicos
  const [soporteN1, setSoporteN1] = useState([]);
  const [soporteN2, setSoporteN2] = useState([]);
  const [supervisores, setSupervisores] = useState([]);

  // Estado para notificaciones
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ✅ PERMISOS
  const rolesPermitidos = ['admin', 'supervisor', 'soporte_n1', 'soporte_n2', 'call_center'];

  const canViewTickets = hasRole(rolesPermitidos) || isAdmin() || isSupervisor() || isSoporte();
  const canCreateTicket = hasRole(rolesPermitidos) || isAdmin() || isSupervisor() || isSoporte();
  const canAssign = isAdmin() || isSupervisor() || hasRole(['soporte_n1', 'soporte_n2']);
  const canResolve = isAdmin() || isSupervisor() || hasRole(['soporte_n1', 'soporte_n2']);
  const canClose = isAdmin() || isSupervisor() || hasRole(['soporte_n1', 'soporte_n2']);
  const canEscalate = isAdmin() || isSupervisor() || hasRole(['soporte_n1', 'soporte_n2']);
  const canEdit = isAdmin() || isSupervisor();
  const canDelete = isAdmin() || isSupervisor();

  // =============== CARGA DE DATOS INICIAL ===============

  useEffect(() => {
    if (canViewTickets) {
      loadTickets();
      loadStats();
      loadTechnicians();
      if (isAdmin() || isSupervisor()) {
        loadTechniciansList();
      }
    }
  }, [tabValue, filterStatus, filterCanal, filterProblema, filterPrioridad, filterTechnician]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {};

      if (tabValue > 0) {
        const statusMap = {
          1: 'nuevo',
          2: 'asignado_n1',
          3: 'asignado_n2',
          4: 'en_progreso',
          5: 'escalado_n2',
          6: 'resuelto',
          7: 'cerrado'
        };
        params.status = statusMap[tabValue];
      }

      if (filterStatus) params.status = filterStatus;
      if (filterCanal) params.canal_contacto = filterCanal;
      if (filterProblema) params.tipo_problema = filterProblema;
      if (filterPrioridad) params.prioridad = filterPrioridad;
      if (searchTerm) params.search = searchTerm;
      
      if (filterTechnician) {
        params.technician_id = parseInt(filterTechnician);
      }

      if (isSoporte() && !isAdmin() && !isSupervisor()) {
        params.technician_id = user?.id;
      }

      const data = await getCallCenterTickets(params);
      setTickets(data);
    } catch (error) {
      console.error('Error cargando tickets:', error);
      showSnackbar('Error al cargar los tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getCallCenterStats();
      setStats(data);

      const techData = await getTechnicianStats();
      setTechStats(techData);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const loadTechnicians = async () => {
    try {
      const data = await getUsuarios();

      setSoporteN1(data.filter(u => u.roles?.some(r => r.name === 'soporte_n1')));
      setSoporteN2(data.filter(u => u.roles?.some(r => r.name === 'soporte_n2')));
      setSupervisores(data.filter(u => u.roles?.some(r => r.name === 'supervisor')));
    } catch (error) {
      console.error('Error cargando técnicos:', error);
    }
  };

  const loadTechniciansList = async () => {
    try {
      const data = await getUsuarios();
      const techs = data.filter(u => 
        u.roles?.some(r => ['soporte_n1', 'soporte_n2', 'supervisor', 'tecnico_campo'].includes(r.name))
      );
      setTechniciansList(techs);
    } catch (error) {
      console.error('Error cargando técnicos:', error);
    }
  };

  // =============== BÚSQUEDA DE CLIENTES ===============
  const searchCustomersHandler = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomers([]);
      return;
    }

    setCustomerSearchLoading(true);
    try {
      const data = await searchCustomers(searchTerm);
      console.log('📋 Datos de clientes recibidos:', data);

      if (Array.isArray(data) && data.length > 0) {
        const formattedData = data.map(customer => ({
          ...customer,
          displayName: customer.nombres && customer.apellidos
            ? `${customer.nombres} ${customer.apellidos}`
            : customer.nombres || customer.identificacion || '',
          customer_name: customer.nombres && customer.apellidos
            ? `${customer.nombres} ${customer.apellidos}`
            : customer.nombres || '',
          customer_phone: customer.telefono || '',
          customer_phone_alt: '',
          customer_email: customer.email || ''
        }));
        setCustomers(formattedData);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error buscando clientes:', error);
      setCustomers([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const loadCustomerHistory = async (customerId) => {
    try {
      const data = await getCustomerTickets(customerId);
      setCustomerHistory(data);
      setCustomerHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error cargando historial del cliente:', error);
      showSnackbar('Error al cargar el historial del cliente', 'error');
    }
  };

  // =============== ACCIONES ===============

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setFilterStatus('');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setFilterCanal('');
    setFilterProblema('');
    setFilterPrioridad('');
    setFilterTechnician('');
    setTabValue(0);
  };

  const handleCreateTicket = async () => {
    try {
      console.log('=== DATOS DEL TICKET A ENVIAR ===');
      console.log('customer_id:', newTicket.customer_id);
      console.log('customer_name:', newTicket.customer_name);
      console.log('customer_phone:', newTicket.customer_phone);
      console.log('customer_phone_alt:', newTicket.customer_phone_alt);
      console.log('title:', newTicket.title);
      console.log('description:', newTicket.description);
      console.log('canal_contacto:', newTicket.canal_contacto);
      console.log('tipo_problema:', newTicket.tipo_problema);
      console.log('prioridad:', newTicket.prioridad);

      if (!newTicket.customer_id) {
        showSnackbar('Por favor selecciona un cliente', 'warning');
        return;
      }

      const customerId = parseInt(newTicket.customer_id);
      if (isNaN(customerId) || customerId <= 0) {
        showSnackbar('ID de cliente inválido', 'error');
        return;
      }

      const ticketData = {
        customer_id: customerId,
        customer_name: newTicket.customer_name.trim(),
        customer_phone: newTicket.customer_phone.trim(),
        customer_phone_alt: newTicket.customer_phone_alt?.trim() || '',
        title: newTicket.title.trim(),
        description: newTicket.description.trim(),
        canal_contacto: newTicket.canal_contacto,
        tipo_problema: newTicket.tipo_problema,
        prioridad: newTicket.prioridad,
      };

      if (newTicket.assign_to_n1_id) {
        ticketData.assign_to_n1_id = parseInt(newTicket.assign_to_n1_id);
      }
      if (newTicket.assign_to_n2_id) {
        ticketData.assign_to_n2_id = parseInt(newTicket.assign_to_n2_id);
      }

      console.log('📤 Enviando ticket:', JSON.stringify(ticketData, null, 2));

      const response = await createCallCenterTicket(ticketData);
      console.log('✅ Respuesta:', response);

      showSnackbar('Ticket creado exitosamente');
      setCreateDialogOpen(false);
      resetNewTicket();
      loadTickets();
      loadStats();
    } catch (error) {
      console.error('❌ Error creando ticket:', error);
      console.error('❌ Detalle:', error.response?.data);

      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const messages = error.response.data.detail.map(e =>
            `${e.loc.join('.')}: ${e.msg}`
          ).join('\n');
          showSnackbar(`Error: ${messages}`, 'error');
        } else {
          showSnackbar(`Error: ${error.response.data.detail}`, 'error');
        }
      } else {
        showSnackbar('Error al crear el ticket. Verifica los datos.', 'error');
      }
    }
  };

  const handleAssign = async () => {
    try {
      const ticketId = selectedTicket?.id;

      if (assignType === 'n1') {
        await assignToN1(ticketId, {
          technician_id: assignData.technician_id,
          notes: assignData.notes
        });
      } else if (assignType === 'n2') {
        await assignToN2(ticketId, {
          technician_id: assignData.technician_id,
          notes: assignData.notes
        });
      } else if (assignType === 'supervisor') {
        await assignToSupervisor(ticketId, {
          technician_id: assignData.technician_id,
          notes: assignData.notes
        });
      }

      showSnackbar(`Ticket asignado exitosamente a ${assignType.toUpperCase()}`);
      setAssignDialogOpen(false);
      resetAssignData();
      loadTickets();
      loadStats();
    } catch (error) {
      console.error('Error asignando ticket:', error);
      showSnackbar('Error al asignar el ticket', 'error');
    }
  };

  const handleEscalate = async (type) => {
    try {
      const ticketId = selectedTicket?.id;

      if (type === 'n2') {
        if (!assignData.technician_id) {
          showSnackbar('Por favor selecciona un técnico N2', 'warning');
          return;
        }
        if (!assignData.reason) {
          showSnackbar('Por favor ingresa el motivo de escalación', 'warning');
          return;
        }
        
        const payload = {
          technician_id: parseInt(assignData.technician_id),
          reason: assignData.reason,
          notes: assignData.notes || ''
        };
        
        console.log('📤 Escalando a N2 con payload:', JSON.stringify(payload, null, 2));
        
        await escalateToN2(ticketId, payload);
        
        showSnackbar(`Ticket escalado exitosamente a N2`);
        setAssignDialogOpen(false);
        resetAssignData();
        loadTickets();
        loadStats();
      } else if (type === 'supervisor') {
        if (!assignData.technician_id) {
          showSnackbar('Por favor selecciona un Supervisor', 'warning');
          return;
        }
        if (!assignData.reason) {
          showSnackbar('Por favor ingresa el motivo de escalación', 'warning');
          return;
        }
        
        await escalateToSupervisor(ticketId, {
          reason: assignData.reason,
          notes: assignData.notes
        });
        
        showSnackbar(`Ticket escalado exitosamente a Supervisor`);
        setAssignDialogOpen(false);
        resetAssignData();
        loadTickets();
        loadStats();
      }
    } catch (error) {
      console.error('❌ Error escalando ticket:', error);
      console.error('❌ Response:', error.response);
      console.error('❌ Data:', error.response?.data);
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const messages = error.response.data.detail.map(e => 
            `Campo: ${e.loc.join('.')} - ${e.msg}`
          ).join('\n');
          showSnackbar(`Error: ${messages}`, 'error');
        } else {
          showSnackbar(`Error: ${error.response.data.detail}`, 'error');
        }
      } else {
        showSnackbar('Error al escalar el ticket', 'error');
      }
    }
  };

  const handleResolve = async () => {
    try {
      await resolveCallCenterTicket(selectedTicket.id, resolveData);
      showSnackbar('Ticket resuelto exitosamente');
      setResolveDialogOpen(false);
      resetResolveData();
      loadTickets();
      loadStats();
    } catch (error) {
      console.error('Error resolviendo ticket:', error);
      showSnackbar('Error al resolver el ticket', 'error');
    }
  };

  const handleClose = async () => {
    try {
      await closeCallCenterTicket(selectedTicket.id, closeData);
      showSnackbar('Ticket cerrado exitosamente');
      setCloseDialogOpen(false);
      resetCloseData();
      loadTickets();
      loadStats();
    } catch (error) {
      console.error('Error cerrando ticket:', error);
      showSnackbar('Error al cerrar el ticket', 'error');
    }
  };

  const handleAddInteraction = async () => {
    try {
      await addInteraction(selectedTicket.id, {
        tipo: interactionData.tipo,
        description: interactionData.description,
        duracion_segundos: interactionData.duracion_segundos ? parseInt(interactionData.duracion_segundos) : null,
        resultado: interactionData.resultado || null
      });
      showSnackbar('Interacción agregada exitosamente');
      setInteractionDialogOpen(false);
      resetInteractionData();
      loadTickets();
    } catch (error) {
      console.error('Error agregando interacción:', error);
      showSnackbar('Error al agregar la interacción', 'error');
    }
  };

  const handleEditTicket = async () => {
    try {
      await updateCallCenterTicket(selectedTicket.id, editData);
      showSnackbar('Ticket actualizado exitosamente');
      setEditDialogOpen(false);
      loadTickets();
      loadStats();
    } catch (error) {
      console.error('Error editando ticket:', error);
      showSnackbar('Error al editar el ticket', 'error');
    }
  };

  const handleDeleteTicket = async () => {
    try {
      await deleteCallCenterTicket(selectedTicket.id);
      showSnackbar('Ticket eliminado exitosamente');
      setDeleteDialogOpen(false);
      loadTickets();
      loadStats();
    } catch (error) {
      console.error('Error eliminando ticket:', error);
      showSnackbar('Error al eliminar el ticket', 'error');
    }
  };

  // =============== RESET DE FORMULARIOS ===============

  const resetNewTicket = () => {
    setNewTicket({
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_phone_alt: '',
      title: '',
      description: '',
      canal_contacto: 'llamada',
      tipo_problema: 'conexion',
      prioridad: 'media',
      assign_to_n1_id: '',
      assign_to_n2_id: ''
    });
    setCustomers([]);
  };

  const resetAssignData = () => {
    setAssignData({
      technician_id: '',
      notes: '',
      reason: ''
    });
  };

  const resetResolveData = () => {
    setResolveData({
      resolution_notes: '',
      satisfaction_score: 5,
      quality_score: 5,
      resolution_type: 'estandar'
    });
  };

  const resetCloseData = () => {
    setCloseData({
      closure_notes: '',
      satisfaction_score: 5,
      follow_up_required: false,
      follow_up_date: null,
      follow_up_notes: ''
    });
  };

  const resetInteractionData = () => {
    setInteractionData({
      tipo: 'nota',
      description: '',
      duracion_segundos: '',
      resultado: ''
    });
  };

  // =============== RENDER DE ESTADÍSTICAS MEJORADAS ===============

  const renderStats = () => {
    if (!stats) return null;

    const totalTickets = stats.total_tickets || 0;
    const resueltos = stats.tickets_resueltos || 0;
    const cerrados = stats.tickets_cerrados || 0;
    const tasaExito = totalTickets > 0 ? ((resueltos + cerrados) / totalTickets * 100) : 0;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tickets
              </Typography>
              <Typography variant="h4">{totalTickets}</Typography>
              <Typography variant="caption" color="textSecondary">
                Últimos 30 días
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nuevos / En Progreso
              </Typography>
              <Typography variant="h4">
                {stats.tickets_nuevos || 0} / {stats.tickets_en_progreso || 0}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Pendientes de atención
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Resueltos / Cerrados
              </Typography>
              <Typography variant="h4">
                {resueltos} / {cerrados}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  Tasa de éxito:
                </Typography>
                <Chip 
                  label={`${tasaExito.toFixed(1)}%`} 
                  size="small" 
                  color={tasaExito >= 80 ? 'success' : tasaExito >= 50 ? 'warning' : 'error'}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderLeft: '4px solid #ed6c02' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Satisfacción Promedio
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4">
                  {stats.satisfaccion_promedio ? stats.satisfaccion_promedio.toFixed(1) : 'N/A'}
                </Typography>
                <Rating 
                  value={stats.satisfaccion_promedio || 0} 
                  readOnly 
                  precision={0.5}
                  size="medium"
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Tasa primer contacto: {stats.tasa_primer_contacto || 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderTechStats = () => {
    if (!techStats || techStats.length === 0) {
      return (
        <Paper sx={{ p: 2, mb: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No hay datos de rendimiento disponibles
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            📊 Rendimiento por Técnico
          </Typography>
          <Chip 
            label={`${techStats.length} técnicos`} 
            size="small" 
            color="info" 
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Técnico</TableCell>
                <TableCell align="center">Rol</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Resueltos</TableCell>
                <TableCell align="center">Cerrados</TableCell>
                <TableCell align="center">Tasa %</TableCell>
                <TableCell align="center">Escalados</TableCell>
                <TableCell align="center">Tiempo Prom.</TableCell>
                <TableCell align="center">Satisfacción</TableCell>
                <TableCell align="center">Calidad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {techStats.map((tech) => (
                <TableRow key={tech.technician_id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                        {tech.technician_name?.charAt(0).toUpperCase()}
                      </Avatar>
                      {tech.technician_name}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={tech.role}
                      size="small"
                      color={tech.role === 'supervisor' ? 'error' : tech.role === 'soporte_n2' ? 'secondary' : 'primary'}
                    />
                  </TableCell>
                  <TableCell align="center">{tech.total_tickets || 0}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={tech.tickets_resueltos || 0} 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={tech.tickets_cerrados || 0} 
                      size="small" 
                      color="secondary" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ minWidth: 60 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={tech.tasa_resolucion || 0} 
                        color={tech.tasa_resolucion >= 80 ? 'success' : tech.tasa_resolucion >= 50 ? 'warning' : 'error'}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption">
                        {(tech.tasa_resolucion || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={tech.tickets_escalados || 0} 
                      size="small" 
                      color={tech.tickets_escalados > 5 ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {tech.tiempo_promedio_resolucion_minutos || 0} min
                  </TableCell>
                  <TableCell align="center">
                    {tech.satisfaccion_promedio ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Rating 
                          value={tech.satisfaccion_promedio} 
                          readOnly 
                          precision={0.5}
                          size="small"
                        />
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          {tech.satisfaccion_promedio.toFixed(1)}
                        </Typography>
                      </Box>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {tech.quality_score_promedio ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <StarIcon sx={{ color: '#ffd700', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          {tech.quality_score_promedio.toFixed(1)}
                        </Typography>
                      </Box>
                    ) : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  // =============== RENDER DE TICKETS ===============

  const renderTicketsTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (tickets.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography color="textSecondary">
            No hay tickets que coincidan con los filtros
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Canal</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Prioridad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Asignado a</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.map((ticket) => (
              <TableRow key={ticket.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {ticket.ticket_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{ticket.customer_name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {ticket.customer_phone}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{ticket.title}</TableCell>
                <TableCell>
                  <Tooltip title={canalLabels[ticket.canal_contacto]}>
                    <Box>
                      {canalIcons[ticket.canal_contacto]}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    label={problemTypeLabels[ticket.tipo_problema]?.split(' ')[0] || ticket.tipo_problema}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={ticket.prioridad}
                    color={priorityColors[ticket.prioridad]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusLabels[ticket.status]}
                    color={statusColors[ticket.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {ticket.assigned_n1_name && (
                    <Chip
                      label={`N1: ${ticket.assigned_n1_name}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {ticket.assigned_n2_name && (
                    <Chip
                      label={`N2: ${ticket.assigned_n2_name}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                  {ticket.assigned_supervisor_name && (
                    <Chip
                      label={`Sup: ${ticket.assigned_supervisor_name}`}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                  {!ticket.assigned_n1_name && !ticket.assigned_n2_name && !ticket.assigned_supervisor_name && (
                    <Typography variant="caption" color="textSecondary">
                      Sin asignar
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(ticket.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Tooltip title="Ver detalle">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedTicket(ticket);
                          setDialogOpen(true);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>

                    {ticket.customer_id && (
                      <Tooltip title="Historial del cliente">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => loadCustomerHistory(ticket.customer_id)}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {canEdit && (
                      <Tooltip title="Editar ticket">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setEditData({
                              title: ticket.title || '',
                              description: ticket.description || '',
                              canal_contacto: ticket.canal_contacto || '',
                              tipo_problema: ticket.tipo_problema || '',
                              prioridad: ticket.prioridad || '',
                              status: ticket.status || ''
                            });
                            setEditDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {canDelete && (
                      <Tooltip title="Eliminar ticket">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {canAssign && ticket.status === 'nuevo' && (
                      <Tooltip title="Asignar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setAssignType('n1');
                            setAssignDialogOpen(true);
                          }}
                        >
                          <AssignmentIndIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {canEscalate && ticket.status === 'asignado_n1' && (
                      <Tooltip title="Escalar a N2">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setAssignType('n2');
                            setAssignDialogOpen(true);
                          }}
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {canResolve && ['asignado_n1', 'asignado_n2', 'en_progreso'].includes(ticket.status) && (
                      <Tooltip title="Resolver">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setResolveDialogOpen(true);
                          }}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    {canClose && ticket.status === 'resuelto' && (
                      <Tooltip title="Cerrar">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setCloseDialogOpen(true);
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Agregar interacción">
                      <IconButton
                        size="small"
                        color="default"
                        onClick={() => {
                          setSelectedTicket(ticket);
                          setInteractionDialogOpen(true);
                        }}
                      >
                        <TimelineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // =============== RENDER ===============

  if (!canViewTickets) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            ⛔ Acceso Denegado
          </Typography>
          <Typography variant="body1" color="textSecondary">
            No tienes permisos para acceder al módulo de Call Center.
            <br />
            Contacta al administrador si crees que deberías tener acceso.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            📞 Call Center
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Gestión de tickets de soporte y atención al cliente
          </Typography>
        </Box>

        {canCreateTicket && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Nuevo Ticket
          </Button>
        )}
      </Box>

      {/* ✅ NOTIFICACIONES DE SEGUIMIENTO */}
      <FollowUpNotifications onTicketUpdate={() => { loadTickets(); loadStats(); }} />

      {/* Estadísticas */}
      {renderStats()}
      {renderTechStats()}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Canal</InputLabel>
              <Select
                value={filterCanal}
                onChange={(e) => setFilterCanal(e.target.value)}
                label="Canal"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="llamada">📞 Llamada</MenuItem>
                <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                <MenuItem value="correo">📧 Correo</MenuItem>
                <MenuItem value="chat">💭 Chat</MenuItem>
                <MenuItem value="presencial">🏢 Presencial</MenuItem>
                <MenuItem value="redes_sociales">🌐 Redes Sociales</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Prioridad</InputLabel>
              <Select
                value={filterPrioridad}
                onChange={(e) => setFilterPrioridad(e.target.value)}
                label="Prioridad"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="baja">Baja</MenuItem>
                <MenuItem value="media">Media</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="critica">Crítica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* ✅ Filtro por técnico - solo admin y supervisor */}
          {(isAdmin() || isSupervisor()) && (
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Filtrar por Técnico</InputLabel>
                <Select
                  value={filterTechnician}
                  onChange={(e) => setFilterTechnician(e.target.value)}
                  label="Filtrar por Técnico"
                >
                  <MenuItem value="">Todos los técnicos</MenuItem>
                  {techniciansList.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.full_name || tech.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={4} md={2}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              size="small"
              fullWidth
            >
              Limpiar
            </Button>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button
              variant="contained"
              onClick={loadTickets}
              startIcon={<SearchIcon />}
              size="small"
              fullWidth
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Todos" />
          <Tab label="Nuevos" />
          <Tab label="Asignados N1" />
          <Tab label="Asignados N2" />
          <Tab label="En Progreso" />
          <Tab label="Escalados" />
          <Tab label="Resueltos" />
          <Tab label="Cerrados" />
        </Tabs>
      </Paper>

      {/* Tabla de tickets */}
      {renderTicketsTable()}

      {/* =============== DIÁLOGOS =============== */}

      {/* Dialog de detalle del ticket */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                Ticket {selectedTicket?.ticket_number}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedTicket?.title}
              </Typography>
            </Box>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Información del Cliente
                  </Typography>
                  <Box display="flex" alignItems="center" mb={1}>
                    <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{selectedTicket.customer_name}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={1}>
                    <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{selectedTicket.customer_phone}</Typography>
                    {selectedTicket.customer_phone_alt && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({selectedTicket.customer_phone_alt})
                      </Typography>
                    )}
                  </Box>
                  {selectedTicket.customer_email && (
                    <Box display="flex" alignItems="center">
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">{selectedTicket.customer_email}</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Detalles del Ticket
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Canal</Typography>
                      <Typography variant="body2">
                        {canalLabels[selectedTicket.canal_contacto]}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Tipo de Problema</Typography>
                      <Typography variant="body2">
                        {problemTypeLabels[selectedTicket.tipo_problema]}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Descripción</Typography>
                      <Typography variant="body2">{selectedTicket.description}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Estados y Asignaciones
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Estado</Typography>
                      <Chip
                        label={statusLabels[selectedTicket.status]}
                        color={statusColors[selectedTicket.status]}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Prioridad</Typography>
                      <Chip
                        label={selectedTicket.prioridad}
                        color={priorityColors[selectedTicket.prioridad]}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Creado</Typography>
                      <Typography variant="body2">
                        {new Date(selectedTicket.created_at).toLocaleString()}
                      </Typography>
                    </Grid>
                    {selectedTicket.resolved_at && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Resuelto</Typography>
                        <Typography variant="body2">
                          {new Date(selectedTicket.resolved_at).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                    {selectedTicket.closed_at && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Cerrado</Typography>
                        <Typography variant="body2">
                          {new Date(selectedTicket.closed_at).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {selectedTicket.satisfaction_score && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Calificación del Cliente
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Rating value={selectedTicket.satisfaction_score} readOnly precision={0.5} />
                      <Typography variant="body2">
                        {satisfactionLabels[selectedTicket.satisfaction_score] || `${selectedTicket.satisfaction_score} estrellas`}
                      </Typography>
                    </Box>
                    {selectedTicket.customer_feedback && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Comentario: {selectedTicket.customer_feedback}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              )}

              {selectedTicket.interactions && selectedTicket.interactions.length > 0 && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Interacciones ({selectedTicket.interactions.length})
                    </Typography>
                    <List dense>
                      {selectedTicket.interactions.map((interaction, idx) => (
                        <ListItem key={idx} divider={idx < selectedTicket.interactions.length - 1}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              {interaction.tipo === 'llamada' && <PhoneIcon />}
                              {interaction.tipo === 'whatsapp' && <WhatsAppIcon />}
                              {interaction.tipo === 'correo' && <EmailIcon />}
                              {interaction.tipo === 'chat' && <ChatIcon />}
                              {interaction.tipo === 'nota' && <TimelineIcon />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={interaction.description}
                            secondary={`${interaction.user_name} - ${new Date(interaction.created_at).toLocaleString()}`}
                          />
                          {interaction.duracion_segundos && (
                            <Chip
                              label={`${interaction.duracion_segundos}s`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {interaction.resultado && (
                            <Chip
                              label={interaction.resultado}
                              size="small"
                              color={interaction.resultado === 'resuelto' ? 'success' : interaction.resultado === 'pendiente' ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de edición */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>✏️ Editar Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descripción"
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Canal</InputLabel>
                <Select
                  value={editData.canal_contacto}
                  onChange={(e) => setEditData({ ...editData, canal_contacto: e.target.value })}
                  label="Canal"
                >
                  <MenuItem value="llamada">📞 Llamada</MenuItem>
                  <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                  <MenuItem value="correo">📧 Correo</MenuItem>
                  <MenuItem value="chat">💭 Chat</MenuItem>
                  <MenuItem value="presencial">🏢 Presencial</MenuItem>
                  <MenuItem value="redes_sociales">🌐 Redes Sociales</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={editData.tipo_problema}
                  onChange={(e) => setEditData({ ...editData, tipo_problema: e.target.value })}
                  label="Tipo"
                >
                  <MenuItem value="conexion">🔌 Conexión</MenuItem>
                  <MenuItem value="velocidad">⚡ Velocidad</MenuItem>
                  <MenuItem value="equipo">📡 Equipo</MenuItem>
                  <MenuItem value="facturacion">💰 Facturación</MenuItem>
                  <MenuItem value="soporte_tecnico">🛠️ Soporte Técnico</MenuItem>
                  <MenuItem value="configuracion">⚙️ Configuración</MenuItem>
                  <MenuItem value="reclamo">📢 Reclamo</MenuItem>
                  <MenuItem value="consulta">❓ Consulta</MenuItem>
                  <MenuItem value="instalacion">🔧 Instalación</MenuItem>
                  <MenuItem value="otro">📝 Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Prioridad</InputLabel>
                <Select
                  value={editData.prioridad}
                  onChange={(e) => setEditData({ ...editData, prioridad: e.target.value })}
                  label="Prioridad"
                >
                  <MenuItem value="baja">🟢 Baja</MenuItem>
                  <MenuItem value="media">🟡 Media</MenuItem>
                  <MenuItem value="alta">🟠 Alta</MenuItem>
                  <MenuItem value="critica">🔴 Crítica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  label="Estado"
                >
                  <MenuItem value="nuevo">Nuevo</MenuItem>
                  <MenuItem value="en_espera">En Espera</MenuItem>
                  <MenuItem value="asignado_n1">Asignado N1</MenuItem>
                  <MenuItem value="asignado_n2">Asignado N2</MenuItem>
                  <MenuItem value="en_progreso">En Progreso</MenuItem>
                  <MenuItem value="resuelto">Resuelto</MenuItem>
                  <MenuItem value="cerrado">Cerrado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEditTicket}>
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>⚠️ Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el ticket <strong>{selectedTicket?.ticket_number}</strong>?
            <br />
            <br />
            Esta acción <strong style={{ color: 'red' }}>NO se puede deshacer</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteTicket}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de creación de ticket */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon color="primary" />
                Nuevo Ticket de Call Center
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Complete todos los campos para crear un nuevo ticket
              </Typography>
            </Box>
            <IconButton onClick={() => setCreateDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Buscador de cliente */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" />
                Datos del Cliente
              </Typography>
              <Autocomplete
                freeSolo
                options={customers}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.displayName || option?.nombres || option?.customer_name || '';
                }}
                loading={customerSearchLoading}
                onInputChange={async (event, newInputValue) => {
                  if (newInputValue && newInputValue.length >= 2) {
                    await searchCustomersHandler(newInputValue);
                  } else {
                    setCustomers([]);
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const customerId = newValue.id ? parseInt(newValue.id) : '';
                    setNewTicket({
                      ...newTicket,
                      customer_id: customerId,
                      customer_name: newValue.customer_name || newValue.displayName || `${newValue.nombres} ${newValue.apellidos}`,
                      customer_phone: newValue.customer_phone || newValue.telefono || '',
                      customer_phone_alt: newValue.customer_phone_alt || ''
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Cliente *"
                    placeholder="Ingresa nombre, apellido, teléfono o cédula"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {option.displayName || `${option.nombres} ${option.apellidos}` || option.customer_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {option.telefono || option.customer_phone || ''}
                        {option.identificacion ? ` - ${option.identificacion}` : ''}
                      </Typography>
                      {option.direccion && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          📍 {option.direccion}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Grid>

            {/* Datos del cliente */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del Cliente *"
                value={newTicket.customer_name}
                onChange={(e) => setNewTicket({ ...newTicket, customer_name: e.target.value })}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono *"
                value={newTicket.customer_phone}
                onChange={(e) => setNewTicket({ ...newTicket, customer_phone: e.target.value })}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono Alternativo"
                value={newTicket.customer_phone_alt}
                onChange={(e) => setNewTicket({ ...newTicket, customer_phone_alt: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Detalles del problema */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Detalles del Problema
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Incidencia *"
                placeholder="Ej: Problema de conexión a internet"
                value={newTicket.title}
                onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descripción *"
                placeholder="Describa el problema en detalle..."
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                required
              />
            </Grid>

            {/* Selecciones */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Clasificación del Problema
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Canal de Contacto *</InputLabel>
                <Select
                  value={newTicket.canal_contacto}
                  onChange={(e) => setNewTicket({ ...newTicket, canal_contacto: e.target.value })}
                  label="Canal de Contacto *"
                >
                  <MenuItem value="llamada">📞 Llamada</MenuItem>
                  <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                  <MenuItem value="correo">📧 Correo</MenuItem>
                  <MenuItem value="chat">💭 Chat</MenuItem>
                  <MenuItem value="presencial">🏢 Presencial</MenuItem>
                  <MenuItem value="redes_sociales">🌐 Redes Sociales</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Problema *</InputLabel>
                <Select
                  value={newTicket.tipo_problema}
                  onChange={(e) => setNewTicket({ ...newTicket, tipo_problema: e.target.value })}
                  label="Tipo de Problema *"
                >
                  <MenuItem value="conexion">🔌 Conexión</MenuItem>
                  <MenuItem value="velocidad">⚡ Velocidad</MenuItem>
                  <MenuItem value="equipo">📡 Equipo</MenuItem>
                  <MenuItem value="facturacion">💰 Facturación</MenuItem>
                  <MenuItem value="soporte_tecnico">🛠️ Soporte Técnico</MenuItem>
                  <MenuItem value="configuracion">⚙️ Configuración</MenuItem>
                  <MenuItem value="reclamo">📢 Reclamo</MenuItem>
                  <MenuItem value="consulta">❓ Consulta</MenuItem>
                  <MenuItem value="instalacion">🔧 Instalación</MenuItem>
                  <MenuItem value="otro">📝 Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Prioridad *</InputLabel>
                <Select
                  value={newTicket.prioridad}
                  onChange={(e) => setNewTicket({ ...newTicket, prioridad: e.target.value })}
                  label="Prioridad *"
                >
                  <MenuItem value="baja">🟢 Baja</MenuItem>
                  <MenuItem value="media">🟡 Media</MenuItem>
                  <MenuItem value="alta">🟠 Alta</MenuItem>
                  <MenuItem value="critica">🔴 Crítica</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Asignación inicial */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Asignación Inicial (Opcional)
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Asignar a N1</InputLabel>
                <Select
                  value={newTicket.assign_to_n1_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewTicket({ 
                      ...newTicket, 
                      assign_to_n1_id: value ? parseInt(value) : '' 
                    });
                  }}
                  label="Asignar a N1"
                >
                  <MenuItem value="">Sin asignar</MenuItem>
                  {soporteN1.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      👤 {tech.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Asignar a N2</InputLabel>
                <Select
                  value={newTicket.assign_to_n2_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewTicket({ 
                      ...newTicket, 
                      assign_to_n2_id: value ? parseInt(value) : '' 
                    });
                  }}
                  label="Asignar a N2"
                >
                  <MenuItem value="">Sin asignar</MenuItem>
                  {soporteN2.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      👤 {tech.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateTicket}
            disabled={!newTicket.customer_id || !newTicket.customer_name || !newTicket.title || !newTicket.description}
            startIcon={<CheckCircleIcon />}
          >
            Crear Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de asignación - CORREGIDO */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {assignType === 'n1' && 'Asignar a Soporte N1'}
          {assignType === 'n2' && 'Escalar a Soporte N2'}
          {assignType === 'supervisor' && 'Escalar a Supervisor'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            
            {/* ✅ PARA N1: Mostrar técnicos N1 */}
            {assignType === 'n1' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Técnico N1 *</InputLabel>
                  <Select
                    value={assignData.technician_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAssignData({ ...assignData, technician_id: value ? parseInt(value) : '' });
                    }}
                    label="Técnico N1 *"
                  >
                    <MenuItem value="">Seleccionar técnico N1</MenuItem>
                    {soporteN1.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id}>
                        {tech.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* ✅ PARA N2: Mostrar técnicos N2 */}
            {assignType === 'n2' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Técnico N2 *</InputLabel>
                  <Select
                    value={assignData.technician_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAssignData({ ...assignData, technician_id: value ? parseInt(value) : '' });
                    }}
                    label="Técnico N2 *"
                  >
                    <MenuItem value="">Seleccionar técnico N2</MenuItem>
                    {soporteN2.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id}>
                        {tech.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* ✅ PARA SUPERVISOR: Mostrar supervisores */}
            {assignType === 'supervisor' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Supervisor *</InputLabel>
                  <Select
                    value={assignData.technician_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAssignData({ ...assignData, technician_id: value ? parseInt(value) : '' });
                    }}
                    label="Supervisor *"
                  >
                    <MenuItem value="">Seleccionar Supervisor</MenuItem>
                    {supervisores.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id}>
                        {tech.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Notas */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notas"
                value={assignData.notes}
                onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
              />
            </Grid>

            {/* ✅ Motivo de escalación - SOLO para N2 y Supervisor */}
            {(assignType === 'n2' || assignType === 'supervisor') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Motivo de escalación *"
                  required
                  value={assignData.reason}
                  onChange={(e) => setAssignData({ ...assignData, reason: e.target.value })}
                  placeholder="Describa el motivo por el cual se escala"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={assignType === 'n2' || assignType === 'supervisor' ? () => handleEscalate(assignType) : handleAssign}
            disabled={
              !assignData.technician_id || 
              ((assignType === 'n2' || assignType === 'supervisor') && !assignData.reason)
            }
          >
            {assignType === 'n2' || assignType === 'supervisor' ? 'Escalar' : 'Asignar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de resolución */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolver Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notas de Resolución"
                required
                value={resolveData.resolution_notes}
                onChange={(e) => setResolveData({ ...resolveData, resolution_notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Resolución</InputLabel>
                <Select
                  value={resolveData.resolution_type}
                  onChange={(e) => setResolveData({ ...resolveData, resolution_type: e.target.value })}
                  label="Tipo de Resolución"
                >
                  <MenuItem value="estandar">Estándar</MenuItem>
                  <MenuItem value="primer_contacto">Primer Contacto</MenuItem>
                  <MenuItem value="escalado">Escalado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Satisfacción del Cliente</InputLabel>
                <Select
                  value={resolveData.satisfaction_score}
                  onChange={(e) => setResolveData({ ...resolveData, satisfaction_score: parseInt(e.target.value) })}
                  label="Satisfacción del Cliente"
                >
                  <MenuItem value={1}>⭐ Muy Malo</MenuItem>
                  <MenuItem value={2}>⭐⭐ Malo</MenuItem>
                  <MenuItem value={3}>⭐⭐⭐ Regular</MenuItem>
                  <MenuItem value={4}>⭐⭐⭐⭐ Bueno</MenuItem>
                  <MenuItem value={5}>⭐⭐⭐⭐⭐ Excelente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResolve}
            disabled={!resolveData.resolution_notes}
          >
            Resolver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de cierre */}
      <Dialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cerrar Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notas de Cierre"
                value={closeData.closure_notes}
                onChange={(e) => setCloseData({ ...closeData, closure_notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Satisfacción del Cliente</InputLabel>
                <Select
                  value={closeData.satisfaction_score}
                  onChange={(e) => setCloseData({ ...closeData, satisfaction_score: parseInt(e.target.value) })}
                  label="Satisfacción del Cliente"
                >
                  <MenuItem value={1}>⭐ Muy Malo</MenuItem>
                  <MenuItem value={2}>⭐⭐ Malo</MenuItem>
                  <MenuItem value={3}>⭐⭐⭐ Regular</MenuItem>
                  <MenuItem value={4}>⭐⭐⭐⭐ Bueno</MenuItem>
                  <MenuItem value={5}>⭐⭐⭐⭐⭐ Excelente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Requiere Seguimiento</InputLabel>
                <Select
                  value={closeData.follow_up_required ? 'si' : 'no'}
                  onChange={(e) => setCloseData({ ...closeData, follow_up_required: e.target.value === 'si' })}
                  label="Requiere Seguimiento"
                >
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="si">Sí</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {closeData.follow_up_required && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Fecha de Seguimiento"
                  type="date"
                  value={closeData.follow_up_date || ''}
                  onChange={(e) => setCloseData({ ...closeData, follow_up_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {closeData.follow_up_required && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notas de Seguimiento"
                  value={closeData.follow_up_notes}
                  onChange={(e) => setCloseData({ ...closeData, follow_up_notes: e.target.value })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cerrar Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de interacción */}
      <Dialog
        open={interactionDialogOpen}
        onClose={() => setInteractionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar Interacción</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={interactionData.tipo}
                  onChange={(e) => setInteractionData({ ...interactionData, tipo: e.target.value })}
                  label="Tipo"
                >
                  <MenuItem value="llamada">📞 Llamada</MenuItem>
                  <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                  <MenuItem value="correo">📧 Correo</MenuItem>
                  <MenuItem value="chat">💭 Chat</MenuItem>
                  <MenuItem value="nota">📝 Nota Interna</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descripción"
                required
                value={interactionData.description}
                onChange={(e) => setInteractionData({ ...interactionData, description: e.target.value })}
              />
            </Grid>
            {interactionData.tipo === 'llamada' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duración (segundos)"
                  type="number"
                  value={interactionData.duracion_segundos}
                  onChange={(e) => setInteractionData({ ...interactionData, duracion_segundos: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Resultado</InputLabel>
                <Select
                  value={interactionData.resultado}
                  onChange={(e) => setInteractionData({ ...interactionData, resultado: e.target.value })}
                  label="Resultado"
                >
                  <MenuItem value="">Sin resultado</MenuItem>
                  <MenuItem value="resuelto">✅ Resuelto</MenuItem>
                  <MenuItem value="pendiente">⏳ Pendiente</MenuItem>
                  <MenuItem value="escalado">⬆️ Escalado</MenuItem>
                  <MenuItem value="no_resuelto">❌ No Resuelto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInteractionDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAddInteraction}
            disabled={!interactionData.description}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de historial del cliente */}
      <Dialog
        open={customerHistoryDialogOpen}
        onClose={() => setCustomerHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              📋 Historial de Tickets del Cliente
            </Typography>
            <IconButton onClick={() => setCustomerHistoryDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {customerHistory.length === 0 ? (
            <Typography color="textSecondary" align="center">
              Este cliente no tiene tickets registrados
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ticket</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Prioridad</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerHistory.map((ticket) => (
                    <TableRow key={ticket.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {ticket.ticket_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{ticket.title}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[ticket.status]}
                          color={statusColors[ticket.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.prioridad}
                          color={priorityColors[ticket.prioridad]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setCustomerHistoryDialogOpen(false);
                            setDialogOpen(true);
                          }}
                        >
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerHistoryDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
